import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteValue,
  Chip,
  TextField,
  createFilterOptions,
} from '@mui/material';
import { useId } from 'react';

import { FormField, colors } from 'common';

import { useReactFlowViewContext } from '../../../context/context';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';

/**
 * This is an internal type used to support `Add "new value"` in the dropdown.
 */
interface FreeSoloOption {
  value: string;
  label: string;
  variant?: 'add' | 'remove';
}

const FREE_SOLO_FILTER = createFilterOptions<FreeSoloOption>();

interface FieldProps
  extends
    Omit<
      AutocompleteProps<FreeSoloOption, true, false, true>,
      'renderInput' | 'multiple' | 'disableClearable' | 'freeSolo' | 'value' | 'options'
    >,
    ConfigFieldProps {
  label: string;
  helperText?: string;
  // Options defaults to empty array if not provided
  options?: string[];
}

/**
 * Based on Freesolo with Creatable example from MUI
 * https://mui.com/material-ui/react-autocomplete/#creatable
 * Note that we also have multiple selection enabled, which adds more complexity.
 */
export function BlockFreeSoloMultiAutocomplete(props: FieldProps) {
  const { fieldName, label, getOptionLabel, helperText, options = [], ...fieldProps } = props;
  const { value, visibleError, setValue } = useSelectedNodeField<
    AutocompleteValue<string, true, false, true> | undefined
  >(fieldName);
  const chosenValues: string[] = value ?? [];
  const { isEditable } = useReactFlowViewContext();

  const fieldId = useId();
  return (
    <FormField
      id={fieldId}
      label={label}
      helperText={helperText ?? 'Pick from available options or type to add a new one'}
      error={visibleError}
    >
      <Autocomplete<FreeSoloOption, true, false, true>
        id={fieldId}
        {...fieldProps}
        freeSolo
        multiple
        selectOnFocus
        clearOnBlur
        disabled={!isEditable}
        value={chosenValues.map((value) => ({ value, label: value }))}
        slotProps={
          /**
           * Required to address type issue with freeSolo and multiple.
           */
          undefined
        }
        onChange={(_, newOptions) => {
          setValue([
            ...new Set(
              newOptions.map((option) => {
                if (typeof option === 'string') {
                  return option;
                }
                return option.value;
              })
            ),
          ]);
        }}
        options={options.map((option) => ({
          value: option,
          label: option,
        }))}
        filterOptions={(options, params) => {
          const filtered = FREE_SOLO_FILTER(options, params);

          const { inputValue } = params;
          if (inputValue !== '') {
            const isExisting = chosenValues.some((chosenValue) => inputValue === chosenValue);
            filtered.push({
              value: inputValue,
              label: `${isExisting ? 'Remove' : 'Add'} "${inputValue}"`,
              variant: isExisting ? 'remove' : 'add',
            });
          }
          return filtered;
        }}
        renderInput={(params) => <TextField {...params} size="small" />}
        renderOption={(props, option, state) => (
          /**
           * `renderOption` is required to ensure that the filtered items have unique keys.
           * See this article: https://lockstep.io/blog/debugging-lesson-well-learned-fixing-material-ui-autocomplete-search/
           */
          <li
            {...props}
            key={state.index}
            style={{
              ...props.style,
              color:
                option.variant === 'add'
                  ? colors.success.dark
                  : option.variant === 'remove'
                    ? colors.error.dark
                    : props?.style?.color,
              backgroundColor:
                option.variant === 'add'
                  ? colors.success.light
                  : option.variant === 'remove'
                    ? colors.error.light
                    : props?.style?.backgroundColor,
            }}
          >
            {getOptionLabel?.(option.label) ?? option.label}
          </li>
        )}
        renderValue={(value, getItemProps) => {
          return value.map((option, index) => {
            const optionValue = typeof option === 'string' ? option : option.value;
            const optionLabel = typeof option === 'string' ? option : option.label;
            return (
              <Chip
                {...getItemProps({ index })}
                key={optionLabel}
                label={optionLabel}
                onDelete={() => {
                  const newValues = chosenValues.filter((fieldValue) => fieldValue !== optionValue);
                  setValue(newValues);
                }}
                sx={{ margin: 0.25 }}
              />
            );
          });
        }}
        getOptionKey={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.label;
        }}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionDisabled={() => false}
        groupBy={() => ''}
        onHighlightChange={() => {
          // Do nothing
        }}
      />
    </FormField>
  );
}
